import React, { useState } from "react";
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import FontAwesome from "@expo/vector-icons/FontAwesome";
import { useRouter } from "expo-router";

import UserImageBaseCard from "./UserImageBaseCard";

import { type AppUserSession } from "@/schemas/auth/session";
import InteractionService from "@/services/user/interaction";
import { type UserLikableImageMedia } from "@/schemas/user/image";
import { formatNumberForDisplay } from "@/formatting/number";
import { prepareImageDetailsNavigation } from "@/utils/router";
import { Spacings } from "@/styling/spacings";

type Props = {
  session: AppUserSession;
  userLikableImage: UserLikableImageMedia;
  isReadOnly: boolean;
  numColumns?: number;
  isPixelated?: boolean;
};

function UserImageActionableCard({
  session,
  userLikableImage,
  numColumns = 2,
  isPixelated = false,
}: Props): React.JSX.Element {
  const imageActionBarHeight = 27;
  const { width } = useWindowDimensions();
  const itemWidth = (width - Spacings.s1 * (numColumns + 1)) / numColumns;
  const containerHeight = (itemWidth / 3) * 4 + imageActionBarHeight;

  const interactionService = new InteractionService(session);
  const router = useRouter();

  const [isLiked, setIsLiked] = useState(userLikableImage.isLiked);
  const [likeCount, setLikeCount] = useState(userLikableImage.image.likeCount);

  const toggleLike = async (): Promise<void> => {
    if (!isLiked) {
      setIsLiked(true);
      setLikeCount(likeCount + 1);
      await interactionService.like(userLikableImage.image);
    }
  };

  const toggleUnlike = async (): Promise<void> => {
    if (isLiked) {
      setIsLiked(false);
      if (likeCount > 0) {
        setLikeCount(likeCount - 1);
      }
      await interactionService.unlike(userLikableImage.image);
    }
  };

  function goToImageDetailsScreen(): void {
    const { navigationParams } =
      prepareImageDetailsNavigation(userLikableImage);
    router.push(navigationParams);
  }

  return (
    <TouchableOpacity
      onPress={goToImageDetailsScreen}
      style={{ height: containerHeight }}
    >
      <UserImageBaseCard
        userLikableImage={userLikableImage}
        numColumns={numColumns}
        isPixelated={isPixelated}
        hasActionBar={true}
      />

      <View style={[styles.imageActionBar, { height: imageActionBarHeight }]}>
        <View style={styles.actionItem}>
          <TouchableOpacity
            onPress={() => (isLiked ? toggleUnlike() : toggleLike())}
          >
            <View style={styles.actionItemIcon}>
              <FontAwesome
                name={isLiked ? "heart" : "heart-o"}
                size={20}
                color={isLiked ? "red" : "black"}
              />
            </View>
          </TouchableOpacity>
          {likeCount > 0 && (
            <TouchableOpacity onPress={goToImageDetailsScreen}>
              <Text style={styles.actionItemCount}>
                {formatNumberForDisplay(likeCount)}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  imageActionBar: {
    backgroundColor: "#fff",
    flexDirection: "row",
    justifyContent: "flex-start",
    borderColor: "#ddd",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderBottomLeftRadius: 7,
    borderBottomRightRadius: 7,
  },
  actionItem: {
    alignItems: "center",
    flexDirection: "row",
  },
  actionItemIcon: {
    paddingLeft: 8,
    paddingRight: 5,
    paddingVertical: 3,
  },
  actionItemCount: {
    width: 50,
    textAlign: "left",
    paddingLeft: 5,
    paddingVertical: 4,
  },
});

export default UserImageActionableCard;
